import { Stack } from '@mui/material'
import { CourseCollapseItem } from 'app/imports/App.components'
import { UILearnArticleImg, useCollapseContext } from 'features/UI'
import { useEffectOnce } from 'hooks/useEffectOnce'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
  ArticleType,
  CourseTimelineItem,
  ETimelinePostStatus,
  ETimelinePostType,
  LessonTimelineItem,
  QuestionnaireTimelineItem,
} from 'types'
import { TimelineExamItem } from '../TimelineExamItem/TimelineExamItem'
import { TimelineLessonItem } from '../TimelineLessonItem/TimelineLessonItem'
import { TimelineLine } from '../TimelineLine/TimelineLine'

export function TimelineCourseItem({
  item,
  isForceExpanded,
}: {
  item: CourseTimelineItem
  isForceExpanded: boolean
}) {
  const { journeyItemType, journeyItemId } = useParams()
  const { setActiveCollapse } = useCollapseContext()

  const lessonsNotDone = useMemo(() => {
    const items = item.items.filter(
      (itemInCourse) =>
        itemInCourse.type === ETimelinePostType.lessons &&
        itemInCourse.status !== ETimelinePostStatus.done,
    ) as LessonTimelineItem[]

    const statusOrder = {
      [ETimelinePostStatus.review]: 0,
      [ETimelinePostStatus.retake]: 1,
      [ETimelinePostStatus.new]: 2,
      [ETimelinePostStatus.done]: 3,
    }

    return items.sort((a, b) => statusOrder[a.status] - statusOrder[b.status])
  }, [item])

  const lessonsDone = useMemo(() => {
    return item.items.filter(
      (itemInCourse) =>
        itemInCourse.type === ETimelinePostType.lessons &&
        itemInCourse.status === ETimelinePostStatus.done,
    ) as LessonTimelineItem[]
  }, [item])

  const allLessonsDone = !lessonsNotDone.length

  const exams = useMemo(() => {
    return item.items.filter(
      (itemInCourse) => itemInCourse.type === ETimelinePostType.questionnaire,
    ) as QuestionnaireTimelineItem[]
  }, [item])

  const childIds = useMemo(() => {
    return lessonsNotDone
      .concat(lessonsDone)
      .map((itemInCourse) => (itemInCourse as LessonTimelineItem).lessonId)
  }, [lessonsDone, lessonsNotDone])

  useEffectOnce(() => {
    const isLessonUrl = journeyItemType === ArticleType.Lesson
    const hasChildInCourse = childIds.includes(Number(journeyItemId))
    if (isLessonUrl && hasChildInCourse) {
      setActiveCollapse(item.id)
    }
  })

  return (
    <Stack direction="row" className="gap-16">
      <div style={{ position: 'relative', minWidth: 30, minHeight: '100%' }}>
        <UILearnArticleImg isOnTimeline />
        <TimelineLine />
      </div>

      <CourseCollapseItem
        id={item.id}
        name={item.name}
        bannerUrl={item.bannerUrl}
        status={item.status}
        isForceExpanded={isForceExpanded}
      >
        {lessonsNotDone.map((itemInCourse) => (
          <TimelineLessonItem key={`${itemInCourse.type}_${itemInCourse.id}`} item={itemInCourse} />
        ))}
        {exams.map((itemInCourse) => (
          <TimelineExamItem
            disabled={!allLessonsDone && itemInCourse.status !== ETimelinePostStatus.retake}
            key={`${itemInCourse.type}_${itemInCourse.id}`}
            item={itemInCourse}
          />
        ))}
        {lessonsDone.map((itemInCourse) => (
          <TimelineLessonItem key={`${itemInCourse.type}_${itemInCourse.id}`} item={itemInCourse} />
        ))}
      </CourseCollapseItem>
    </Stack>
  )
}
